import type {Component, Editor} from 'grapesjs';
import {setComponentStyle, toggleComponentStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {parseCountdownFlag, refreshCountdown, setCountdownFlag} from '@EmailMakerReact/lib/email-maker-countdown-library';
import type {CountdownPlacement} from '@EmailMakerReact/lib/email-maker-countdown-library.types';
import {
  COUNTDOWN_ATTRIBUTES,
  COUNTDOWN_DEFAULT_LABEL_COLOR,
  COUNTDOWN_DEFAULT_LABEL_SIZE,
  COUNTDOWN_PLACEMENT,
  COUNTDOWN_SEPARATOR,
  COUNTDOWN_STYLE_PROPS,
  COUNTDOWN_WIDTH_FIT_CONTENT,
} from '@EmailMakerReact/lib/email-maker-countdown-library.types';
import type {ToggleSwitchDescriptor} from '../../controls/toggle-switch-control/toggle-switch-control.types';
import type {ColorControlDescriptor} from '../../controls/color-control/color-control.types';
import type {DropdownSelectorDescriptor} from '../../controls/dropdown-selector-control/dropdown-selector-control.types';
import type {FontSelectorDescriptor} from '../../controls/font-selector-control/font-selector-control.types';
import {FONT_FAMILY_PROPERTY} from '../../controls/font-selector-control/font-selector-control.types';
import type {OptionListDescriptor} from '../../controls/option-list-control/option-list-control.types';
import type {SliderControlDescriptor} from '../../controls/slider-control/slider-control.types';
import type {UploadButtonDescriptor} from '../../controls/upload-button-control/upload-button-control.types';
import {backgroundImageDescriptor} from '../../controls/upload-button-control/upload-button-control-descriptors';
import {widthDescriptor} from '../../controls/slider-control/slider-control-descriptors';

const FONT_WEIGHT_PROPERTY = 'font-weight';
const BACKGROUND_COVER_STYLES: Record<string, string> = {'background-size': 'cover', 'background-position': 'center', 'background-repeat': 'no-repeat'};
const FONT_WEIGHT_BOLD = 'bold';
const FONT_WEIGHT_NORMAL = 'normal';
const LABEL_SIZE_MIN_PX = 4;
const LABEL_SIZE_MAX_PX = 40;

function makeCountdownFlagToggleDescriptor(attribute: string, labelKey: string): ToggleSwitchDescriptor {
  return {
    attribute,
    labelKey,
    isOn: (attributeValue: string): boolean => parseCountdownFlag(attributeValue, true),
    onToggle: (_editor: Editor, component: Component, isOn: boolean): void => {
      setCountdownFlag(component, attribute, isOn);
    },
  };
}

export const showDaysToggleDescriptor = makeCountdownFlagToggleDescriptor(COUNTDOWN_ATTRIBUTES.SHOW_DAYS, 'pmwjs_show_days');
export const showLabelsToggleDescriptor = makeCountdownFlagToggleDescriptor(COUNTDOWN_ATTRIBUTES.SHOW_LABELS, 'pmwjs_show_labels');

export function areLabelsShown(_component: Component, showLabelsValue: string): boolean {
  return showLabelsToggleDescriptor.isOn(showLabelsValue);
}

export const countdownBackgroundImageDescriptor: UploadButtonDescriptor = {
  ...backgroundImageDescriptor,
  onUpload(editor: Editor, component: Component, onMediaSelected: (url: string) => void): void {
    backgroundImageDescriptor.onUpload(editor, component, (url: string): void => {
      for (const [property, value] of Object.entries(BACKGROUND_COVER_STYLES)) {
        setComponentStyle(editor, {property, value, component});
      }
      onMediaSelected(url);
    });
  },
};

export const numbersBoldDescriptor: OptionListDescriptor = {
  property: FONT_WEIGHT_PROPERTY,
  titleKey: 'pmwjs_style',
  toggleable: true,
  list: [
    {
      value: FONT_WEIGHT_BOLD,
      name: 'pmwjs_bold',
      classes: 'icon-bold',
      callback: (_component: Component, editor: Editor): void => {
        toggleComponentStyle(editor, {property: FONT_WEIGHT_PROPERTY, activeValue: FONT_WEIGHT_BOLD, defaultValue: FONT_WEIGHT_NORMAL});
      },
    },
  ],
};

export const separatorDescriptor: DropdownSelectorDescriptor = {
  property: COUNTDOWN_STYLE_PROPS.SEPARATOR,
  labelKey: 'pmwjs_separator',
  defaultValue: COUNTDOWN_SEPARATOR.COLON,
  options: [
    {textKey: 'pmwjs_colon', value: COUNTDOWN_SEPARATOR.COLON},
    {textKey: 'pmwjs_dot', value: COUNTDOWN_SEPARATOR.DOT},
    {textKey: 'pmwjs_none', value: COUNTDOWN_SEPARATOR.NONE},
  ],
  onDropDownItemChange: (component: Component, _property: string, _value: string, _editor: Editor): void => {
    refreshCountdown(component);
  },
};

export const labelSizeDescriptor: SliderControlDescriptor = {
  property: COUNTDOWN_STYLE_PROPS.LABEL_SIZE,
  labelKey: 'pmwjs_font_size',
  unit: 'px',
  min: LABEL_SIZE_MIN_PX,
  max: LABEL_SIZE_MAX_PX,
  step: 1,
  defaults: COUNTDOWN_DEFAULT_LABEL_SIZE,
};

export const labelColorDescriptor: ColorControlDescriptor = {
  property: COUNTDOWN_STYLE_PROPS.LABEL_COLOR,
  labelKey: 'pmwjs_text_color',
  isDeletable: false,
  defaultColor: COUNTDOWN_DEFAULT_LABEL_COLOR.replace('#', ''),
  onColorChange: (_color: string, component: Component, _editor: Editor): void => {
    refreshCountdown(component);
  },
};

export const labelFontDescriptor: FontSelectorDescriptor = {
  property: COUNTDOWN_STYLE_PROPS.LABEL_FONT,
  fallbackProperty: FONT_FAMILY_PROPERTY,
  labelKey: 'pmwjs_font',
  onFontChange: (component: Component, _editor: Editor): void => {
    refreshCountdown(component);
  },
};

export const countdownWidthDescriptor: SliderControlDescriptor = {
  ...widthDescriptor,
  defaults: COUNTDOWN_WIDTH_FIT_CONTENT,
};

function setNumbersAlignment(editor: Editor, component: Component, placement: CountdownPlacement): void {
  setComponentStyle(editor, {property: COUNTDOWN_STYLE_PROPS.PLACEMENT, value: placement, component});
  refreshCountdown(component);
}

export const numbersAlignmentDescriptor: OptionListDescriptor = {
  property: COUNTDOWN_STYLE_PROPS.PLACEMENT,
  titleKey: 'pmwjs_alignment',
  defaults: COUNTDOWN_PLACEMENT.SPREAD,
  list: [
    {
      value: COUNTDOWN_PLACEMENT.SPREAD,
      name: 'pmwjs_stretch',
      classes: 'icon-stretch-horizontal',
      callback: (component: Component, editor: Editor): void => {
        setNumbersAlignment(editor, component, COUNTDOWN_PLACEMENT.SPREAD);
      },
    },
    {
      value: COUNTDOWN_PLACEMENT.LEFT,
      name: 'pmwjs_left',
      classes: 'icon-h-align-left',
      callback: (component: Component, editor: Editor): void => {
        setNumbersAlignment(editor, component, COUNTDOWN_PLACEMENT.LEFT);
      },
    },
    {
      value: COUNTDOWN_PLACEMENT.CENTER,
      name: 'pmwjs_center',
      classes: 'icon-h-align-center',
      callback: (component: Component, editor: Editor): void => {
        setNumbersAlignment(editor, component, COUNTDOWN_PLACEMENT.CENTER);
      },
    },
    {
      value: COUNTDOWN_PLACEMENT.RIGHT,
      name: 'pmwjs_right',
      classes: 'icon-h-align-right',
      callback: (component: Component, editor: Editor): void => {
        setNumbersAlignment(editor, component, COUNTDOWN_PLACEMENT.RIGHT);
      },
    },
  ],
};
